import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface DateTimePickerProps {
  startDate: Date;
  endDate: Date;
  onDateTimeChange: (startDate: Date, endDate: Date) => void;
  label?: string;
}

const DateTimePicker = ({ startDate, endDate, onDateTimeChange, label = "Started At" }: DateTimePickerProps) => {
  const [ currentMonth, setCurrentMonth ] = useState(new Date(startDate));
  const [ startDateSelected, setStartDateSelected ] = useState<Date | null>(new Date(startDate));
  const [ endDateSelected, setEndDateSelected ] = useState<Date | null>(new Date(endDate));
  const [ isOpen, setIsOpen ] = useState(false);
  const [ calendarPosition, setCalendarPosition ] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);

  const formatDateRange = (start: Date, end: Date): string => {
    const startStr = start.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit'
    });
    const endStr = end.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit'
    });
    return `${startStr} 12:00 AM - ${endStr} 11:59 PM`;
  };

  // Calendar Functions
  const getDaysInMonth = (year: number, month: number): Date[] => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days: Date[] = [];

    const firstDayOfWeek = firstDay.getDay();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      days.push(new Date(year, month, -i));
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    const lastDayOfWeek = lastDay.getDay();
    for (let i = 1; i <= 6 - lastDayOfWeek; i++) {
      days.push(new Date(year, month + 1, i));
    }

    return days;
  }

  const isInRange = (date: Date, start: Date, end: Date): boolean => date >= start && date <= end;
  const isCurrentMonth = (date: Date): boolean => date.getMonth() === currentMonth.getMonth();
  const days = getDaysInMonth(currentMonth.getFullYear(), currentMonth.getMonth());
  const monthYear = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const isSameDay = (date1: Date, date2: Date): boolean => {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  };

  const getDateClasses = (date: Date): string => {
    const baseClasses = "p-1.5 text-xs rounded transition-colors";

    if (!isCurrentMonth(date)) return `${baseClasses} text-gray-400 hover:bg-brand-50`;

    if (startDateSelected && endDateSelected) {
      const isStart = isSameDay(date, startDateSelected);
      const isEnd = isSameDay(date, endDateSelected);
      const isInSelectedRange = isInRange(date, startDateSelected, endDateSelected);

      if (isStart || isEnd) {
        return `${baseClasses} bg-brand-600 text-white hover:bg-brand-700`;
      } else if (isInSelectedRange) {
        return `${baseClasses} bg-brand-200 text-brand-900 hover:bg-brand-300`;
      }
    } else if (startDateSelected && isSameDay(date, startDateSelected)) {
      return `${baseClasses} bg-brand-600 text-white hover:bg-brand-700`;
    }

    return `${baseClasses} text-gray-900 hover:bg-brand-300`;
  };

  const changeMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const checkPosition = () => {
    if (!containerRef.current) { return; }

    const rect = containerRef.current.getBoundingClientRect();
    const calendarHeight = 300;
    const shouldDisplayUpwards = rect.top + calendarHeight > window.innerHeight;
    const left = rect.left;
    const top = shouldDisplayUpwards ? rect.top - calendarHeight - 32 : rect.bottom;

    setCalendarPosition({ top, left });
  };

  // Event Handlers
  const handleUpdate = () => {
    if (startDateSelected && endDateSelected) {
      const startWithTime = new Date(startDateSelected);
      startWithTime.setHours(0, 0, 0, 0);
      const endWithTime = new Date(endDateSelected);
      endWithTime.setHours(23, 59, 59, 999);

      onDateTimeChange(startWithTime, endWithTime);
    }
    setIsOpen(false);
  };

  const handleDateSelect = (date: Date) => {
    if (!endDateSelected && startDateSelected) {
      if (date < startDateSelected) {
        // If second date is before first date, swap them
        setEndDateSelected(startDateSelected);
        setStartDateSelected(date);
      } else {
        setEndDateSelected(date);
      }
    } else {
      // Reset and start new selection
      setStartDateSelected(date);
      setEndDateSelected(null);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setStartDateSelected(new Date(startDate));
      setEndDateSelected(new Date(endDate));
    } else {
      setCurrentMonth(new Date());
    }
  }, [ isOpen, startDate, endDate ]);

  useEffect(() => {
    if (isOpen) checkPosition();
  }, [isOpen, calendarPosition.top, calendarPosition.left]);

  useEffect(() => {
    const currentContainer = containerRef.current;
    if (!currentContainer) { return; }

    const observer = new ResizeObserver(checkPosition);
    observer.observe(currentContainer);

    window.addEventListener('resize', checkPosition);
    window.addEventListener('scroll', checkPosition);

    return () => {
      observer.unobserve(currentContainer);
      window.removeEventListener('resize', checkPosition);
      window.removeEventListener('scroll', checkPosition);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideContainer = containerRef.current && !containerRef.current.contains(target);
      const isOutsidePortal = portalRef.current && !portalRef.current.contains(target);

      if (isOutsideContainer && isOutsidePortal) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={containerRef}>
      <div className="mb-4">
        <div className="block text-sm font-semibold text-gray-900 mb-2">
          {label}
        </div>
        <div
          className="flex items-center px-3 py-2 border border-brand-500 rounded-lg cursor-pointer bg-white"
          onClick={() => setIsOpen(!isOpen)}
        >
          <CalendarIcon className="h-4 w-4 text-gray-500 mr-2" />
          <span className="text-gray-900">
            {formatDateRange(startDate, endDate)}
          </span>
        </div>
      </div>

      {isOpen && createPortal(
        <div
          ref={portalRef}
          className="fixed z-[60] bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-80"
          style={{
            top: `${calendarPosition.top}px`,
            left: `${calendarPosition.left}px`
          }}
        >
          <div ref={calendarRef} className="mb-3">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => changeMonth('prev')}
                className="p-1 hover:bg-brand-50 rounded"
              >
                <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
              </button>
              <div className="text-center">
                <h3 className="text-base font-semibold text-gray-900">{monthYear}</h3>
                <p className="text-xs text-gray-500">
                  {!startDateSelected ? 'Select start date' : !endDateSelected ? 'Select end date' : 'Range selected'}
                </p>
              </div>
              <button
                onClick={() => changeMonth('next')}
                className="p-1 hover:bg-brand-50 rounded"
              >
                <ChevronRightIcon className="h-4 w-4 text-gray-600" />
              </button>
            </div>

            {/* Days of Week */}
            <div className="grid grid-cols-7 gap-0.5 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs text-gray-500 py-1">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-0.5">
              {days.map((date, index) => (
                <button
                  key={index}
                  onClick={() => handleDateSelect(date)}
                  className={getDateClasses(date)}
                >
                  {date.getDate()}
                </button>
              ))}
            </div>
          </div>

          {/* Update Button */}
          <div className="flex justify-end mt-3">
            <button
              onClick={handleUpdate}
              disabled={!startDateSelected || !endDateSelected}
              className="px-3 py-1.5 text-sm bg-brand-600 text-white rounded hover:bg-brand-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Update
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DateTimePicker;
